/**
 * Runtime config for the Vite frontend.
 *
 * Resolution order, lowest precedence first:
 *   1. Build-time Vite env (`VITE_API_URL`, `VITE_AGENT_CORE_URL`, …)
 *   2. Runtime `/config.json` fetched at app start
 *
 * Build-time wins for local `npm run dev` (no /config.json shipped);
 * runtime wins in production so ops can swap the AgentCore runtime ARN
 * or API base URL by editing `public/config.json` + redeploying without
 * a Vite rebuild.
 */

let runtimeConfig = null;
let configReadyResolve;
export const configReady = new Promise((resolve) => {
  configReadyResolve = resolve;
});

/**
 * Synchronously read the merged config. `loadRuntimeConfig()` should be
 * awaited at app boot so this returns the runtime-overridden values; if
 * called before that, only the build-time values are returned.
 */
export function getConfig() {
  const env = import.meta.env;
  const merged = {
    apiUrl: (env.VITE_API_URL || '').replace(/\/$/, ''),
    agentCoreUrl: env.VITE_AGENT_CORE_URL || env.VITE_AGENT_RUNTIME_URL || env.VITE_AGENT_RUNTIME_ARN || '',
    region: env.VITE_AWS_REGION || 'us-east-1',
    stage: env.VITE_STAGE || 'dev',
    inlineEditStreamUrl: env.VITE_INLINE_EDIT_STREAM_URL || '',
    // Okta groups whose members see the usage-analytics nav entry. The
    // real gate is server-side (ADMIN_GROUPS on /admin/usage); this only
    // controls link visibility.
    adminGroups: (env.VITE_ADMIN_GROUPS || '').split(',').map((g) => g.trim()).filter(Boolean),
    adminEmails: (env.VITE_ADMIN_EMAILS || '').split(',').map((e) => e.trim()).filter(Boolean),
  };
  if (runtimeConfig) {
    if (runtimeConfig.apiUrl) {
      merged.apiUrl = String(runtimeConfig.apiUrl).replace(/\/$/, '');
    }
    if (runtimeConfig.agentCoreUrl || runtimeConfig.agentRuntimeUrl || runtimeConfig.agentRuntimeArn) {
      merged.agentCoreUrl =
        runtimeConfig.agentCoreUrl
        || runtimeConfig.agentRuntimeUrl
        || runtimeConfig.agentRuntimeArn
        || merged.agentCoreUrl;
    }
    if (runtimeConfig.region) merged.region = runtimeConfig.region;
    if (runtimeConfig.stage) merged.stage = runtimeConfig.stage;
    if (runtimeConfig.inlineEditStreamUrl) merged.inlineEditStreamUrl = runtimeConfig.inlineEditStreamUrl;
    if (Array.isArray(runtimeConfig.adminGroups)) merged.adminGroups = runtimeConfig.adminGroups;
    if (Array.isArray(runtimeConfig.adminEmails)) merged.adminEmails = runtimeConfig.adminEmails;
  }
  return merged;
}

/**
 * Fetch /config.json once at boot. Call from main.tsx before mounting
 * the app. Failure is non-fatal — falls back to build-time env.
 */
export async function loadRuntimeConfig() {
  if (runtimeConfig) return runtimeConfig;
  try {
    // 8s cap — a hung fetch here otherwise pins the boot skeleton on
    // screen forever (Boot waits on this promise before rendering).
    const res = await fetch('/config.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined,
    });
    if (!res.ok) throw new Error(`config.json HTTP ${res.status}`);
    runtimeConfig = await res.json();
  } catch (err) {
    console.warn('[config] runtime config.json not loaded:', err?.message || err);
    runtimeConfig = {};
  }
  if (configReadyResolve) configReadyResolve();
  return runtimeConfig;
}
